import { useCallback, useEffect, useRef, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api, { API, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Backup & Pemulihan: the only screen that can back up, restore or replace the whole database. Every action
// is a background job (the server returns at once; this page follows its progress), every destructive step
// asks for the exact target name to be typed, and nothing here claims success before the server says so.

const STATUS_TONE = {
  QUEUED: "bg-slate-100 text-slate-700", RUNNING: "bg-sky-100 text-sky-800", SUCCESS: "bg-emerald-100 text-emerald-800",
  FAILED: "bg-red-100 text-red-800", INTERRUPTED: "bg-amber-100 text-amber-900", REJECTED: "bg-amber-100 text-amber-900",
  VERIFIED: "bg-emerald-100 text-emerald-800", UPLOADED: "bg-slate-100 text-slate-700", INVALID: "bg-red-100 text-red-800",
  INCOMPATIBLE: "bg-red-100 text-red-800", PASS: "bg-emerald-100 text-emerald-800", FAIL: "bg-red-100 text-red-800",
};
const STATUS_LABEL = {
  QUEUED: "Antre", RUNNING: "Berjalan", SUCCESS: "Berhasil", FAILED: "Gagal", INTERRUPTED: "Terputus", REJECTED: "Ditolak",
  VERIFIED: "Terverifikasi", UPLOADED: "Diunggah", INVALID: "Tidak valid", INCOMPATIBLE: "Tidak kompatibel",
};
const KIND_LABEL = {
  BACKUP: "Buat Backup", VERIFY: "Verifikasi Paket", DRY_RUN: "Dry-run", RESTORE_ISOLATED: "Restore ke Target Terisolasi",
  REPLACE: "Replace Database", ROLLBACK: "Rollback", ENGAGEMENT_DRY_RUN: "Dry-run Impor Perikatan",
  ENGAGEMENT_IMPORT: "Impor Perikatan", ENGAGEMENT_IMPORT_ROLLBACK: "Batalkan Impor Perikatan",
};
const ACTIVE = ["QUEUED", "RUNNING"];

function Badge({ value, testid }) {
  if (!value) return <span className="text-slate-400">—</span>;
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[value] || "bg-slate-100"}`}
    data-testid={testid}>{STATUS_LABEL[value] || value}</span>;
}

function detailOf(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return [detail.message, ...(detail.missing || [])].filter(Boolean).join(" ");
  }
  return getErrorMessage(error);
}

const bytes = (n) => n == null ? "—" : n < 1024 ** 2 ? `${(n / 1024).toFixed(1)} KB`
  : n < 1024 ** 3 ? `${(n / 1024 ** 2).toFixed(1)} MB` : `${(n / 1024 ** 3).toFixed(2)} GB`;
const when = (iso) => (iso || "").slice(0, 19).replace("T", " ") || "—";

export default function BackupRecovery() {
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [trail, setTrail] = useState([]);
  const [held, setHeld] = useState([]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [upload, setUpload] = useState(null);
  const [typed, setTyped] = useState({});
  const [engagementId, setEngagementId] = useState("");
  const [loadError, setLoadError] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/db/status");
      setStatus(data);
      setLoadError("");
      if (!data.provisioned) return;
      const [p, j, a, h] = await Promise.all([api.get("/admin/db/packages"), api.get("/admin/db/jobs"),
        api.get("/admin/db/audit"), api.get("/admin/db/held-admins")]);
      setPackages(p.data); setJobs(j.data); setTrail(a.data); setHeld(h.data);
    } catch (e) {
      setLoadError(e?.response?.status === 401
        ? "Sesi Anda dibatalkan karena database dipulihkan atau diganti. Silakan masuk kembali."
        : detailOf(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const running = jobs.some((j) => ACTIVE.includes(j.status));
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [running, load]);

  const openDetail = async (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id);
    try { setDetail((await api.get(`/admin/db/packages/${id}`)).data); } catch (e) { toast.error(detailOf(e)); }
  };
  useEffect(() => {
    if (open && !running) api.get(`/admin/db/packages/${open}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [open, running]);

  const start = async (url, body, label) => {
    try {
      await api.post(url, body || {});
      toast.success(`${label} dimulai; ikuti progresnya di Riwayat Proses.`);
    } catch (e) {
      toast.error(detailOf(e));
    }
    await load();
  };

  const sendUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pilih berkas paket (.tar) terlebih dahulu.");
    if (status?.max_upload_bytes && file.size > status.max_upload_bytes) {
      return toast.error(`Berkas ${bytes(file.size)} melebihi batas unggah ${bytes(status.max_upload_bytes)}.`);
    }
    setUpload({ name: file.name, pct: 0 });
    try {
      await api.post(`/admin/db/packages/upload?filename=${encodeURIComponent(file.name)}`, file, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (event) => setUpload({ name: file.name, pct: event.total ? Math.round((100 * event.loaded) / event.total) : 0 }),
      });
      toast.success("Paket diunggah; verifikasi checksum berjalan otomatis.");
      fileRef.current.value = "";
    } catch (e) {
      toast.error(detailOf(e));
    }
    setUpload(null);
    await load();
  };

  const confirmTyped = (key) => (typed[key] || "").trim();

  if (!status) {
    return <Layout><PageHeader breadcrumb="Arsip & Admin" title="Backup & Pemulihan" />
      <div className="p-6 text-sm text-muted-foreground" data-testid="br-loading">{loadError || "Memuat status database…"}</div></Layout>;
  }

  const state = status.state || {};
  const standby = state.standby || {};
  const pending = state.pending_confirmation;
  const activeDb = state.active_db;

  return (
    <Layout>
      <PageHeader breadcrumb="Arsip & Admin" title="Backup & Pemulihan"
        subtitle="Backup, verifikasi, dry-run, restore terisolasi, replace dan rollback seluruh database beserta berkas bukti" />
      <div className="p-4 sm:p-6 space-y-5" data-testid="backup-recovery-page">
        {loadError && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="br-load-error">{loadError}</p>}
        {!status.provisioned && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-sm" data-testid="br-not-provisioned">
            <p className="font-semibold mb-1">Fitur belum disiapkan di server ini.</p>
            <ul className="list-disc pl-5">{status.missing.map((m) => <li key={m}>{m}</li>)}</ul>
            <p className="mt-2 text-xs">Lihat ops/DATABASE_ADMIN_RUNBOOK.md bagian Penyiapan. Tidak ada kredensial yang perlu ditempel di layar ini.</p>
          </div>
        )}

        {status.provisioned && (
          <section className="grid gap-3 md:grid-cols-4" data-testid="br-status">
            <div className="border rounded-sm bg-white p-3"><div className="text-[11px] text-muted-foreground">Database aktif</div>
              <div className="font-mono text-sm break-all" data-testid="br-active-db">{activeDb}</div></div>
            <div className="border rounded-sm bg-white p-3"><div className="text-[11px] text-muted-foreground">Target restore terisolasi</div>
              <div className="font-mono text-sm break-all" data-testid="br-standby-db">{status.standby_target || "—"}</div>
              <div className="text-[11px] mt-1">Status: <span data-testid="br-standby-status">{standby.status || "kosong"}</span></div></div>
            <div className="border rounded-sm bg-white p-3"><div className="text-[11px] text-muted-foreground">Database sebelumnya (titik rollback)</div>
              <div className="font-mono text-sm break-all">{state.previous_db || "—"}</div></div>
            <div className={`border rounded-sm p-3 ${state.maintenance?.on ? "bg-amber-50 border-amber-300" : "bg-white"}`}>
              <div className="text-[11px] text-muted-foreground">Mode pemeliharaan</div>
              <div className="text-sm font-semibold" data-testid="br-maintenance">{state.maintenance?.on ? "AKTIF (hanya-baca)" : "Tidak aktif"}</div>
              {state.maintenance?.on && <div className="text-[11px]">{state.maintenance.reason}</div>}
            </div>
          </section>
        )}

        {pending && (
          <section className="border border-amber-300 bg-amber-50 rounded-sm p-3 space-y-2" data-testid="br-pending">
            <p className="text-sm font-semibold">Replace menunggu konfirmasi</p>
            <p className="text-xs">Database <span className="font-mono">{activeDb}</span> aktif sejak {when(pending.at)}. Database sebelumnya
              <span className="font-mono"> {pending.previous_db}</span> disimpan utuh sebagai titik rollback sampai Anda mengonfirmasi.</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs"><span className="block mb-1">Ketik <b>ROLLBACK {pending.previous_db}</b></span>
                <Input className="h-8 w-72 max-w-full" value={typed.rollback || ""} onChange={(e) => setTyped({ ...typed, rollback: e.target.value })} data-testid="br-rollback-input" /></label>
              <Button size="sm" variant="destructive" disabled={running || confirmTyped("rollback") !== `ROLLBACK ${pending.previous_db}`}
                onClick={() => start("/admin/db/rollback", { confirmation: confirmTyped("rollback") }, "Rollback")} data-testid="br-rollback">Rollback</Button>
              <label className="text-xs"><span className="block mb-1">Ketik <b>KONFIRMASI {activeDb}</b></span>
                <Input className="h-8 w-72 max-w-full" value={typed.confirm || ""} onChange={(e) => setTyped({ ...typed, confirm: e.target.value })} data-testid="br-confirm-input" /></label>
              <Button size="sm" disabled={running || confirmTyped("confirm") !== `KONFIRMASI ${activeDb}`}
                onClick={async () => { try { await api.post("/admin/db/confirm", { confirmation: confirmTyped("confirm") }); toast.success("Replace dikonfirmasi."); } catch (e) { toast.error(detailOf(e)); } load(); }}
                data-testid="br-confirm">Konfirmasi Replace</Button>
            </div>
          </section>
        )}

        {status.provisioned && state.maintenance?.on && !running && (
          <section className="border border-red-300 bg-red-50 rounded-sm p-3 text-xs space-y-2" data-testid="br-maintenance-stuck">
            <p>Mode pemeliharaan masih aktif tanpa proses yang berjalan (kemungkinan proses terputus). Periksa Riwayat Proses sebelum mengakhirinya.</p>
            <div className="flex flex-wrap items-end gap-2">
              <Input className="h-8 w-60 max-w-full" placeholder="AKHIRI PEMELIHARAAN" value={typed.maintenance || ""}
                onChange={(e) => setTyped({ ...typed, maintenance: e.target.value })} data-testid="br-maintenance-input" />
              <Button size="sm" variant="outline" disabled={confirmTyped("maintenance") !== "AKHIRI PEMELIHARAAN"}
                onClick={async () => { try { await api.post("/admin/db/maintenance/off", { confirmation: "AKHIRI PEMELIHARAAN" }); } catch (e) { toast.error(detailOf(e)); } load(); }}
                data-testid="br-maintenance-off">Akhiri mode pemeliharaan</Button>
            </div>
          </section>
        )}

        {status.provisioned && (
          <section className="flex flex-wrap items-end gap-3 border rounded-sm bg-white p-3" data-testid="br-actions">
            <Button onClick={() => start("/admin/db/backups", {}, "Backup")} disabled={running} data-testid="br-create-backup">Buat Backup</Button>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs"><span className="block text-muted-foreground mb-1">Unggah Paket (.tar, maks. {bytes(status.max_upload_bytes)})</span>
                <Input ref={fileRef} type="file" accept=".tar,application/x-tar" className="h-9 w-72 max-w-full" data-testid="br-upload-file" /></label>
              <Button variant="outline" onClick={sendUpload} disabled={!!upload || running} data-testid="br-upload">Unggah Paket</Button>
            </div>
            {upload && (
              <div className="w-64 max-w-full text-xs" data-testid="br-upload-progress">{upload.name}: {upload.pct}%
                <div className="h-1.5 bg-slate-200 rounded"><div className="h-1.5 bg-sky-600 rounded" style={{ width: `${upload.pct}%` }} /></div></div>
            )}
            {running && <span className="text-xs text-sky-800" data-testid="br-running">Satu proses sedang berjalan; aksi lain menunggu.</span>}
          </section>
        )}

        {status.provisioned && (
          <section className="space-y-2" data-testid="br-packages">
            <h3 className="font-semibold text-sm">Paket</h3>
            {packages.length === 0 && <p className="text-xs text-muted-foreground">Belum ada paket. Buat backup atau unggah paket.</p>}
            <div className="overflow-x-auto"><table className="audit-table w-full text-xs">
              <thead><tr><th>Paket</th><th>Sumber</th><th>Dibuat</th><th>Ukuran</th><th>SHA-256</th><th>Status</th><th>Aksi</th></tr></thead>
              <tbody>{packages.map((p) => {
                const verified = p.status === "VERIFIED";
                const restored = standby.package_id === p.id && standby.status === "VALIDATED";
                return (
                  <tr key={p.id} data-testid={`br-package-${p.id}`}>
                    <td className="break-all"><button className="underline text-left" onClick={() => openDetail(p.id)} data-testid={`br-package-open-${p.id}`}>{p.filename}</button></td>
                    <td>{p.source === "PRE_REPLACE_BACKUP" ? "Backup sebelum replace" : p.source === "UPLOAD" ? "Unggahan" : "Backup"}</td>
                    <td>{when(p.created_at)}</td><td>{bytes(p.bytes)}</td>
                    <td className="font-mono" title={p.sha256}>{(p.sha256 || "").slice(0, 12)}…</td>
                    <td><Badge value={p.status} testid={`br-package-status-${p.id}`} />{restored && <span className="ml-1 text-emerald-700" data-testid={`br-package-restored-${p.id}`}>· dipulihkan & tervalidasi</span>}</td>
                    <td><div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2" disabled={running} onClick={() => start(`/admin/db/packages/${p.id}/verify`, {}, "Verifikasi")} data-testid={`br-verify-${p.id}`}>Verifikasi</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" disabled={running || !verified} onClick={() => start(`/admin/db/packages/${p.id}/dry-run`, {}, "Dry-run")} data-testid={`br-dryrun-${p.id}`}>Dry-run</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" disabled={running || !verified || !!pending} onClick={() => start(`/admin/db/packages/${p.id}/restore-isolated`, {}, "Restore terisolasi")} data-testid={`br-restore-${p.id}`}>Restore ke Target Terisolasi</Button>
                      <a className="inline-flex items-center h-7 px-2 border rounded-md text-xs" href={`${API}/admin/db/packages/${p.id}/download`} data-testid={`br-download-${p.id}`}>Unduh</a>
                    </div></td>
                  </tr>);
              })}</tbody>
            </table></div>
          </section>
        )}

        {detail && open === detail.id && (
          <PackageDetail detail={detail} activeDb={activeDb} standby={standby} running={running} pending={pending}
            typed={typed} setTyped={setTyped} start={start} engagementId={engagementId} setEngagementId={setEngagementId} />
        )}

        {held.length > 0 && (
          <section className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs space-y-2" data-testid="br-held">
            <p className="font-semibold text-sm">Akun administrator dari paket yang ditahan</p>
            <p>Paket impor tidak dapat memberi hak administrator secara otomatis. Lepaskan hanya akun yang memang berhak.</p>
            {held.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-2">
                <span className="font-mono">{u.email}</span><span>peran asal: {u.restore_admin_hold?.original_role}</span>
                <Button size="sm" variant="outline" className="h-7" onClick={async () => { try { await api.post(`/admin/db/held-admins/${u.id}/release`); toast.success("Akun dilepas."); } catch (e) { toast.error(detailOf(e)); } load(); }}
                  data-testid={`br-release-${u.id}`}>Lepaskan penahanan</Button>
              </div>))}
          </section>
        )}

        {status.provisioned && (
          <section className="space-y-2" data-testid="br-jobs">
            <h3 className="font-semibold text-sm">Riwayat Proses</h3>
            <div className="overflow-x-auto"><table className="audit-table w-full text-xs">
              <thead><tr><th>Proses</th><th>Status</th><th>Progres</th><th>Target</th><th>Pelaku</th><th>Mulai</th><th>Hasil / pesan</th></tr></thead>
              <tbody>{jobs.map((j) => (
                <tr key={j.id} data-testid={`br-job-${j.id}`}>
                  <td>{KIND_LABEL[j.kind] || j.kind}</td>
                  <td><Badge value={j.status} testid={`br-job-status-${j.id}`} /></td>
                  <td className="min-w-[140px]">{ACTIVE.includes(j.status) ? (
                    <div><div className="h-1.5 bg-slate-200 rounded"><div className="h-1.5 bg-sky-600 rounded" style={{ width: `${j.progress?.pct || 0}%` }} /></div>
                      <div className="mt-0.5">{j.progress?.pct || 0}% · {j.progress?.step}</div></div>) : (j.progress?.step || "—")}</td>
                  <td className="font-mono break-all">{j.target || "—"}</td>
                  <td className="break-all">{j.actor?.email}</td>
                  <td>{when(j.started_at || j.created_at)}</td>
                  <td className="max-w-[380px]" data-testid={`br-job-message-${j.id}`}>
                    {j.error ? (<span className="text-red-800">{j.error.message}{j.error.action && <span className="block text-slate-700">Tindakan: {j.error.action}</span>}</span>)
                      : j.result ? <span>{summaryOf(j)}</span> : "—"}
                  </td>
                </tr>))}</tbody>
            </table></div>
          </section>
        )}

        {status.provisioned && (
          <section className="space-y-2" data-testid="br-audit">
            <h3 className="font-semibold text-sm">Audit trail administrasi database</h3>
            <p className="text-[11px] text-muted-foreground">Disimpan di database kontrol, sehingga tetap dapat ditelusuri setelah replace atau rollback.</p>
            <div className="overflow-x-auto"><table className="audit-table w-full text-xs">
              <thead><tr><th>Waktu</th><th>Tindakan</th><th>Hasil</th><th>Pelaku</th><th>Target</th><th>Paket</th></tr></thead>
              <tbody>{trail.slice(0, 50).map((a) => (
                <tr key={a.id}><td>{when(a.at)}</td><td>{a.action}</td><td>{a.result}</td><td className="break-all">{a.actor?.email}</td>
                  <td className="font-mono break-all">{a.target || "—"}</td><td className="font-mono">{(a.package_id || "").slice(0, 8)}</td></tr>))}</tbody>
            </table></div>
          </section>
        )}
      </div>
    </Layout>
  );
}

function summaryOf(job) {
  const r = job.result || {};
  if (job.kind === "BACKUP") return `Paket ${bytes(r.bytes)}; ${r.totals?.collections} koleksi, ${r.totals?.documents} dokumen, ${r.totals?.files} berkas; terverifikasi.`;
  if (job.kind === "DRY_RUN") return `${r.id_conflicts} konflik ID, ${r.records_only_in_target} catatan hanya di target, ${r.missing_dependencies} dependensi hilang, ${r.missing_evidence} bukti hilang.`;
  if (job.kind === "RESTORE_ISOLATED") return `Dipulihkan ke ${r.target} dan tervalidasi (${r.totals?.collections} koleksi, ${r.totals?.files} berkas).`;
  if (job.kind === "REPLACE") return `Aktif: ${r.target}; sebelumnya ${r.previous} disimpan. ${r.files_copied} berkas disalin. Menunggu konfirmasi.`;
  if (job.kind === "ROLLBACK") return `Aktif kembali: ${r.target}.`;
  if (job.kind === "VERIFY") return "Checksum, jumlah dan kompatibilitas lolos.";
  if (job.kind === "ENGAGEMENT_DRY_RUN") return `Perikatan ${r.engagement_id}: ${r.status}.`;
  if (job.kind === "ENGAGEMENT_IMPORT") return `Perikatan ${r.engagement_id} diimpor ke ${r.target}: ${r.written} dokumen, ${r.files_created} berkas; backup pra-impor terverifikasi.`;
  if (job.kind === "ENGAGEMENT_IMPORT_ROLLBACK") return `Impor perikatan ${r.engagement_id} dibatalkan: ${r.restored} dipulihkan, ${r.removed} dihapus, ${r.files_removed} berkas dihapus.`;
  return r.status || "Selesai";
}

// B-501: what each referenced file was checked against. A package is usable for restore, replace or import only
// when every file matches the one checksum its records agree on (or a controlled baseline).
export function SourceChecksums({ summary }) {
  const unverified = summary.referenced_files_unverified;
  const conflicts = summary.referenced_files_conflict;
  const missing = summary.referenced_files_missing || [];
  const mismatch = summary.referenced_files_mismatch || [];
  const counts = summary.source_checksums;
  if (unverified == null && conflicts == null) {
    return (
      <p className="text-amber-900" data-testid="br-checksum-legacy">
        Paket ini dibuat sebelum status checksum sumber per catatan dicatat. Paket tidak dianggap terverifikasi untuk itu:
        restore terisolasi memeriksa setiap catatan, dan catatan tanpa checksum sumber yang sah menggagalkan validasinya.
      </p>);
  }
  const problems = (unverified?.length || 0) + (conflicts?.length || 0) + missing.length + mismatch.length;
  return (
    <div className="space-y-1" data-testid="br-checksums">
      {counts && (
        <p data-testid="br-checksum-counts">Checksum sumber: {counts.verified_against_record} berkas cocok dengan checksum catatan
          {counts.verified_against_baseline ? `, ${counts.verified_against_baseline} dengan baseline terkontrol (bukan bukti unggahan asli)` : ""}
          {counts.files_not_verified ? `; ${counts.files_not_verified} berkas belum terverifikasi` : ""}
          {problems ? `; ${problems} masalah` : ""}.</p>)}
      {unverified?.length > 0 && (
        <div className="text-red-800" data-testid="br-checksum-unverified">
          <p className="font-semibold">Catatan tanpa checksum sumber yang dapat dipakai ({unverified.length}) — berkas disalin, tetapi tidak terverifikasi:</p>
          <ul className="list-disc pl-5">{unverified.slice(0, 20).map((u, i) => (
            <li key={i}><span className="font-mono">{u.collection} {u.record_id}</span> · {u.root}/{u.path}: {u.message}</li>))}</ul>
          <p className="text-slate-700">Tindakan: jangan mengisi checksum pada catatan. Bila berkas itu memang yang dimaksud, tetapkan baseline
            terkontrol dengan persetujuan tertulis (Panduan Backup &amp; Pemulihan); baseline tidak membuktikan berkas sama dengan saat diunggah.</p>
        </div>)}
      {conflicts?.length > 0 && (
        <div className="text-red-800" data-testid="br-checksum-conflicts">
          <p className="font-semibold">Berkas yang dirujuk beberapa catatan dengan checksum berbeda ({conflicts.length}):</p>
          <ul className="list-disc pl-5">{conflicts.slice(0, 20).map((c, i) => (
            <li key={i}>{c.root}/{c.path}: {c.records.map((r) => `${r.collection} ${r.record_id}`).join(", ")}</li>))}</ul>
          <p className="text-slate-700">Tindakan: periksa catatan dan berkasnya; keputusan catatan mana yang benar dibuat orang. Sistem tidak memilih salah satunya.</p>
        </div>)}
      {(missing.length > 0 || mismatch.length > 0) && (
        <div className="text-red-800" data-testid="br-checksum-files">
          {missing.length > 0 && <p>Berkas tidak ada di penyimpanan: {missing.slice(0, 10).map((m) => `${m.root}/${m.path}`).join(", ")}</p>}
          {mismatch.length > 0 && <p>Isi berkas berbeda dari checksum catatan: {mismatch.slice(0, 10).map((m) => `${m.root}/${m.path}`).join(", ")}</p>}
          <p className="text-slate-700">Tindakan: pulihkan berkas dari sumber aslinya, lalu buat backup ulang.</p>
        </div>)}
    </div>);
}

function PackageDetail({ detail, activeDb, standby, running, pending, typed, setTyped, start, engagementId, setEngagementId }) {
  const dry = detail.dry_run;
  const restore = detail.restore;
  const summary = detail.summary || {};
  const restored = standby.package_id === detail.id && standby.status === "VALIDATED";
  const phrase = `GANTI ${activeDb}`;
  return (
    <section className="border rounded-sm bg-white p-3 space-y-3 text-xs" data-testid="br-detail">
      <h3 className="font-semibold text-sm break-all">{detail.filename}</h3>
      <div className="grid gap-2 md:grid-cols-3">
        <div>Sumber database: <span className="font-mono">{summary.source_db || "—"}</span></div>
        <div>Dibuat: {when(summary.created_at)} · versi skema {summary.schema_version ?? "—"}</div>
        <div>Konsistensi: {summary.consistency || "—"} · aplikasi {summary.app?.git_sha || "—"}</div>
      </div>
      {detail.verification?.problems?.length > 0 && (
        <div className="text-red-800" data-testid="br-verify-problems">{detail.verification.problems.slice(0, 10).map((p, i) => <div key={i}>{p.message}</div>)}</div>
      )}
      {detail.verification?.compatibility && <p className="text-red-800">{detail.verification.compatibility}</p>}
      <SourceChecksums summary={summary} />

      {dry && (
        <div className="space-y-2" data-testid="br-dryrun-report">
          <p className="font-semibold">Hasil dry-run (tidak ada data yang diubah)</p>
          <p>Sumber <span className="font-mono">{dry.source?.database}</span> ({when(dry.source?.created_at)}) → target <span className="font-mono">{dry.target?.database}</span>.
            Konflik ID: <b data-testid="br-dry-conflicts">{dry.id_conflicts}</b>. Catatan yang hanya ada di target (tidak aktif lagi setelah replace): <b>{dry.records_only_in_target}</b>.</p>
          {dry.missing_dependencies?.length > 0 && <p className="text-amber-900">Dependensi hilang: {dry.missing_dependencies.map((d) => `${d.needs} ${d.missing}`).join(", ")}</p>}
          {dry.missing_evidence?.length > 0 && <p className="text-red-800">Bukti hilang: {dry.missing_evidence.map((m) => m.path).join(", ")}</p>}
          {dry.target_only_collections?.length > 0 && <p className="text-amber-900">Koleksi yang hanya ada di target: {dry.target_only_collections.map((c) => `${c.collection} (${c.target_count})`).join(", ")}</p>}
          <div className="overflow-x-auto"><table className="audit-table w-full">
            <thead><tr><th>Koleksi</th><th>Paket</th><th>Target</th><th>ID sama, isi sama</th><th>ID sama, isi beda</th><th>Hanya di target</th><th>Status</th><th>Alasan</th></tr></thead>
            <tbody>{dry.collections.map((c) => (
              <tr key={c.collection} data-testid={`br-dry-row-${c.collection}`}><td className="font-mono">{c.collection}</td><td>{c.package_count}</td><td>{c.target_count}</td>
                <td>{c.same_id_identical}</td><td>{c.same_id_different}</td><td>{c.only_in_target}</td>
                <td>{c.status === "EXCLUDED" ? "Dikecualikan" : "Didukung"}</td><td>{c.reason || "—"}</td></tr>))}</tbody>
          </table></div>
          <p className="text-muted-foreground">{dry.note}</p>
        </div>
      )}

      {restore && (
        <div className="space-y-1" data-testid="br-restore-report">
          <p className="font-semibold">Restore terisolasi ke <span className="font-mono">{restore.target_db}</span>: <Badge value={restore.validation?.status} testid="br-restore-validation" /></p>
          {restore.validation?.problems?.map((p, i) => <div key={i} className="text-red-800">{p.message}</div>)}
          {restore.validation?.warnings?.map((p, i) => <div key={i} className="text-amber-900">{p.message}</div>)}
          <ul className="list-disc pl-5">{restore.validation?.checks?.map((c) => <li key={c}>{c}</li>)}</ul>
          {Object.keys(restore.outcome?.excluded || {}).length > 0 && (
            <p>Dikecualikan: {Object.entries(restore.outcome.excluded).map(([name, e]) => `${name} (${e.count}): ${e.reason}`).join(" · ")}</p>)}
        </div>
      )}

      {restored && !pending && (
        <div className="border border-red-300 bg-red-50 rounded-sm p-3 space-y-2" data-testid="br-replace-panel">
          <p className="font-semibold text-sm">Replace Database</p>
          <p>Target yang akan diganti: <span className="font-mono font-semibold" data-testid="br-replace-target">{activeDb}</span>. Isinya digantikan oleh paket ini
            {dry ? `; ${dry.records_only_in_target} catatan yang hanya ada di target tidak aktif lagi` : ""}.</p>
          <ul className="list-disc pl-5">
            <li>Mode pemeliharaan (hanya-baca) aktif selama proses; perubahan yang sedang berjalan ditunggu selesai.</li>
            <li>Backup terbaru database aktif dibuat dan diverifikasi otomatis sebelum perpindahan.</li>
            <li>Database lama tidak dihapus; tetap tersedia untuk rollback sampai Anda mengonfirmasi.</li>
            <li>Semua sesi dibatalkan; administrator dari paket ditahan; akun Anda dipertahankan.</li>
          </ul>
          <div className="flex flex-wrap items-end gap-2">
            <label><span className="block mb-1">Ketik <b>{phrase}</b></span>
              <Input className="h-8 w-72 max-w-full" value={typed.replace || ""} onChange={(e) => setTyped({ ...typed, replace: e.target.value })} data-testid="br-replace-input" /></label>
            <Button size="sm" variant="destructive" disabled={running || (typed.replace || "").trim() !== phrase}
              onClick={() => start(`/admin/db/packages/${detail.id}/replace`, { confirmation: phrase }, "Replace")} data-testid="br-replace">Replace Database</Button>
          </div>
        </div>
      )}

      {restored && (
        <div className="border rounded-sm p-3 space-y-2" data-testid="br-engagement-panel">
          <p className="font-semibold">Impor satu perikatan dari paket ini</p>
          <p>Untuk memindahkan satu perikatan ke database aktif <b>{activeDb}</b> tanpa mengganti seluruh database. Jalankan dry-run dulu;
            penerapan hanya tersedia untuk dry-run yang bersih (READY_FOR_APPROVAL) dan menjalankan persis rencana itu: mode pemeliharaan,
            backup terverifikasi database aktif, backup dan log tulis impor, lalu verifikasi rekonsiliasi — gagal di titik mana pun
            dibatalkan otomatis. Pengguna dan akun tidak pernah diimpor; status audit disalin apa adanya. Impor data nyata (mis. Nebraska)
            ke produksi memerlukan persetujuan pemilik.</p>
          <div className="flex flex-wrap items-end gap-2">
            <Input className="h-8 w-80 max-w-full" placeholder="ID perikatan" value={engagementId} onChange={(e) => setEngagementId(e.target.value)} data-testid="br-engagement-id" />
            <Button size="sm" variant="outline" disabled={running || !engagementId.trim()}
              onClick={() => start(`/admin/db/packages/${detail.id}/engagement-dry-run`, { engagement_id: engagementId.trim() }, "Dry-run impor perikatan")}
              data-testid="br-engagement-dryrun">Dry-run Impor Perikatan</Button>
          </div>
          {Object.entries(detail.engagement_dry_runs || {}).map(([id, r]) => {
            const done = (detail.engagement_imports || {})[id];
            const applyPhrase = `IMPOR ${id} KE ${activeDb}`;
            const undoPhrase = `BATALKAN IMPOR ${id}`;
            return (
            <div key={id} className="border-t pt-2 space-y-1" data-testid={`br-engagement-report-${id}`}>
              <div><b>{id}</b>: {r.status}{r.message ? ` — ${r.message}` : ""}
              {r.totals && <span> · sisip {r.totals.INSERT}, ubah {r.totals.UPDATE}, sama {r.totals.SKIP_IDENTICAL}, konflik {r.totals.CONFLICT_FOREIGN_OWNER}</span>}
              {r.collision && <span> · tabrakan identitas: {r.collision.status}</span>}
              {r.evidence_files && <span> · berkas bukti: {r.evidence_files.total}</span>}</div>
              {done && <div data-testid={`br-engagement-import-${id}`}>Impor: <b>{done.status}</b>{done.target ? ` ke ${done.target}` : ""}{done.written != null ? ` · ${done.written} dokumen, ${done.files_created} berkas` : ""}{done.message ? ` — ${done.message}` : ""}</div>}
              {r.status === "READY_FOR_APPROVAL" && done?.status !== "APPLIED" && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-xs">Ketik <code>{applyPhrase}</code>
                    <Input className="h-8 w-80 max-w-full" value={typed[`import-${id}`] || ""} onChange={(e) => setTyped({ ...typed, [`import-${id}`]: e.target.value })} data-testid={`br-engagement-import-input-${id}`} /></label>
                  <Button size="sm" variant="destructive" disabled={running || (typed[`import-${id}`] || "").trim() !== applyPhrase}
                    onClick={() => start(`/admin/db/packages/${detail.id}/engagement-import`, { engagement_id: id, confirmation: (typed[`import-${id}`] || "").trim() }, "Impor perikatan")}
                    data-testid={`br-engagement-apply-${id}`}>Terapkan Impor Perikatan</Button>
                </div>)}
              {done?.status === "APPLIED" && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-xs">Ketik <code>{undoPhrase}</code>
                    <Input className="h-8 w-72 max-w-full" value={typed[`undo-${id}`] || ""} onChange={(e) => setTyped({ ...typed, [`undo-${id}`]: e.target.value })} data-testid={`br-engagement-undo-input-${id}`} /></label>
                  <Button size="sm" variant="outline" disabled={running || (typed[`undo-${id}`] || "").trim() !== undoPhrase}
                    onClick={() => start(`/admin/db/packages/${detail.id}/engagement-import/rollback`, { engagement_id: id, confirmation: (typed[`undo-${id}`] || "").trim() }, "Pembatalan impor perikatan")}
                    data-testid={`br-engagement-undo-${id}`}>Batalkan Impor</Button>
                </div>)}
            </div>);
          })}
        </div>
      )}
    </section>
  );
}
